import { Controller } from '@hotwired/stimulus';
import { t } from '../i18n.js';

export default class extends Controller {
  static values = { type: String, autoload: Boolean, api: String };

  connect() {
    this.key = `doc_help_seen:${this.typeValue}`;
    this.apiUrl = this.apiValue || '/api/user-settings';
    this.seen = this.getSeen();
    if (!this.seen) this.mountModal();
    this.mountReopener();
    if (!this.seen && this.autoloadValue) this.open();
  }

  mountModal() {
    const existing = this.element.querySelector('#doc-help-modal');
    if (existing) {
      this.modal = existing;
      return;
    }
    const wrap = document.createElement('div');
    wrap.id = 'doc-help-modal';
    wrap.className = 'fixed inset-0 hidden z-[10000]';
    wrap.innerHTML = `
      <div class="absolute inset-0 bg-black/40" data-action="click->doc-help#close"></div>
      <div class="absolute left-1/2 top-24 -translate-x-1/2 w-[min(42rem,92vw)] rounded-lg bg-white dark:bg-gray-800 shadow-xl">
        <div class="p-5 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 id="doc-help-title" class="text-lg font-semibold">${t('help_title', 'How this view works')}</h2>
          <button class="text-gray-500 hover:text-gray-800" data-action="doc-help#close">✕</button>
        </div>
        <div id="doc-help-body" class="p-5 text-sm leading-6 text-gray-700 dark:text-gray-200"></div>
        <div class="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button class="px-3 py-1.5 border rounded" data-action="doc-help#close">${t('close', 'Close')}</button>
          <button class="px-3 py-1.5 rounded bg-indigo-600 text-white" data-action="doc-help#gotIt">${t('got_it', 'Got it')}</button>
        </div>
      </div>`;
    this.element.appendChild(wrap);
    this.modal = wrap;
  }

  mountReopener() {
    const existing = this.element.querySelector('#doc-help-reopen');
    if (existing) {
      this.reopener = existing;
      return;
    }
    const btn = document.createElement('button');
    btn.id = 'doc-help-reopen';
    btn.type = 'button';
    btn.className =
      'help-btn fixed left-4 bottom-6 z-[11000] px-3 py-1.5 rounded border bg-white/90 dark:bg-gray-800/90 shadow hover:bg-white';
    btn.textContent = t('help', 'Help');
    btn.setAttribute('data-action', 'click->doc-help#open');
    this.element.appendChild(btn);
    this.reopener = btn;
  }

  open() {
    if (!this.modal) this.mountModal();
    const { title, html } = this.contentFor(this.typeValue);
    this.modal.querySelector('#doc-help-title').textContent = title;
    this.modal.querySelector('#doc-help-body').innerHTML = html;
    this.modal.classList.remove('hidden');
  }

  close() {
    if (this.modal) this.modal.classList.add('hidden');
  }

  gotIt() {
    this.markSeen();
    this.close();
    if (this.modal) {
      this.modal.remove();
      this.modal = null;
    }
  }

  getSeen() {
    const hinted = this.element.dataset.seen === 'true';
    if (hinted) return true;
    return localStorage.getItem(this.key) === '1';
  }

  markSeen() {
    localStorage.setItem(this.key, '1');
    this.element.dataset.seen = 'true';
    this.seen = true;
    // best-effort server persist (if logged in)
    if (!window.currentUserRole) return;
    fetch(this.apiUrl, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') || {}).content || '',
      },
      body: JSON.stringify({ seen_tip_key: this.typeValue }),
      credentials: 'same-origin',
    }).catch(() => {});
  }

  contentFor(type) {
    switch (type) {
      case 'author_standard':
        return {
          title: t('author_documents_help', 'Author documents'),
          html: `
            <ul class="list-disc list-inside space-y-1">
              <li>${t('author_help_1', 'Read shared works straight through.')}</li>
              <li>${t('author_help_2', 'Leave microfeedback to help the author improve.')}</li>
              <li>${t('author_help_3', 'Bookmarks and recaps work the same as your documents.')}</li>
            </ul>`,
        };
      case 'author_checkpointed':
        return {
          title: t('checkpoint_help', 'Checkpointed reading'),
          html: `
            <ul class="list-disc list-inside space-y-1">
              <li>${t('checkpoint_help_1', 'Complete the configured checkpoint after each section.')}</li>
              <li>${t('checkpoint_help_2', 'Each response unlocks only the next section.')}</li>
              <li>${t('progress_saved', 'Your progress is saved automatically.')}</li>
            </ul>`,
        };
      case 'microfeedback':
        return {
          title: t('microfeedback_help', 'Microfeedback'),
          html: `
            <ul class="list-disc list-inside space-y-1">
              <li>${t('feedback_help_1', 'React to each paragraph or leave a note.')}</li>
              <li>${t('feedback_help_2', 'Use Next to move through the piece.')}</li>
              <li>${t('feedback_help_3', 'Authors see aggregated insights; your identity remains private unless you share it.')}</li>
            </ul>`,
        };
      default:
        return { title: t('help_title', 'How this works'), html: `<p>${t('quick_tips', 'Quick tips for this view.')}</p>` };
    }
  }
}
